import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, MapPin, LogOut, Store, Shield, LayoutDashboard } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation } from "@/context/LocationContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Footer } from "@/components/Footer";

export default function Account() {
  const { user, setUser, logout } = useAuth();
  const { loc, requestMyLocation } = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: user.name || "", phone: user.phone || "", area: user.area || "", postcode: user.postcode || "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", form);
      setUser(data);
      toast.success("Profile updated");
    } catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10 min-h-[70vh]">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16 rounded-2xl bg-[#1A3C2A] text-white flex items-center justify-center text-2xl font-bold font-display">{(user.name || "U")[0].toUpperCase()}</div>
        <div>
          <h1 className="font-display text-2xl font-bold text-[#1A3C2A]">{user.name}</h1>
          <p className="text-sm text-[#5A626A]">{user.email} · <span className="capitalize font-semibold">{user.role}</span></p>
        </div>
      </div>

      {(user.role === "seller" || user.role === "admin") && (
        <div className="flex gap-3 mb-6">
          {user.role === "seller" && <Button data-testid="account-go-seller" onClick={() => navigate("/seller")} className="rounded-full bg-[#1A3C2A] hover:bg-[#132E20] gap-2"><LayoutDashboard size={16} /> Seller Dashboard</Button>}
          {user.role === "admin" && <Button data-testid="account-go-admin" onClick={() => navigate("/admin")} className="rounded-full bg-[#1A3C2A] hover:bg-[#132E20] gap-2"><Shield size={16} /> Admin Dashboard</Button>}
        </div>
      )}

      <div className="bg-white border border-[#E5DEC9] rounded-2xl p-5 sm:p-6 space-y-4">
        <h2 className="font-display font-bold text-[#1A3C2A] flex items-center gap-2"><User size={18} /> Profile</h2>
        <div><Label className="text-xs">Full name</Label><Input data-testid="account-name" value={form.name} onChange={set("name")} className="rounded-xl mt-1" /></div>
        <div><Label className="text-xs">Phone</Label><Input data-testid="account-phone" value={form.phone} onChange={set("phone")} className="rounded-xl mt-1" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Area</Label><Input data-testid="account-area" value={form.area} onChange={set("area")} className="rounded-xl mt-1" placeholder="Hackney, London" /></div>
          <div><Label className="text-xs">Postcode</Label><Input data-testid="account-postcode" value={form.postcode} onChange={set("postcode")} className="rounded-xl mt-1" /></div>
        </div>
        <Button data-testid="account-save" onClick={save} disabled={saving} className="rounded-full bg-[#1A3C2A] hover:bg-[#132E20]">{saving ? "Saving…" : "Save changes"}</Button>
      </div>

      <div className="bg-white border border-[#E5DEC9] rounded-2xl p-5 mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-[#5A626A]"><MapPin size={16} className="text-[#E07A5F]" /> {loc ? `Shopping near ${loc.label}` : "No location set"}</div>
        <Button variant="outline" data-testid="account-use-location" onClick={requestMyLocation} className="rounded-full border-[#E5DEC9]">Use my location</Button>
      </div>

      {user.role === "customer" && (
        <div className="bg-[#1A3C2A] rounded-2xl p-5 mt-4 flex items-center justify-between">
          <div><p className="font-display font-bold text-white">Want to sell on Nabor?</p><p className="text-sm text-[#FAF7F2]/80">Open your local shop in minutes.</p></div>
          <Button data-testid="account-start-selling" onClick={() => navigate("/sell")} className="rounded-full bg-[#E07A5F] hover:bg-[#D06447] gap-2"><Store size={16} /> Start</Button>
        </div>
      )}

      <Button variant="outline" data-testid="account-logout" onClick={async () => { await logout(); navigate("/"); }}
        className="w-full rounded-full border-[#E5DEC9] mt-4 gap-2 text-red-600 hover:text-red-700"><LogOut size={16} /> Sign out</Button>
      <div className="mt-16"><Footer /></div>
    </div>
  );
}
